import { useCallback } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateHighlightRequest,
  UpdateHighlightRequest,
  getDocumentHighlights,
  createHighlight,
  updateHighlight,
  deleteHighlight,
  ExtendedHighlight,
} from "@/api/highlights";

/**
 * Convert frontend highlight to backend format for creation
 */
const convertToBackendHighlight = (
  frontendHighlight: ExtendedHighlight,
  documentId: string,
): CreateHighlightRequest => {
  if (!frontendHighlight.position) {
    throw new Error("Position data is required for highlight creation");
  }

  return {
    document: documentId,
    position: frontendHighlight.position,
    content: { text: frontendHighlight.content?.text || "" },
    comment: frontendHighlight.comment || { text: "", emoji: "" },
    type: frontendHighlight.type,
  };
};

/**
 * Hook for managing persistent user highlights in PDF documents.
 * 
 * This hook provides a complete interface for CRUD operations on highlights that are
 * persisted to the backend database. It handles user permissions, loading states,
 * and automatic cache invalidation. These are distinct from temporary citation highlights
 * which are generated from Q&A data.
 * 
 * @param documentId - The ID of the document to manage highlights for
 * @param currentUserId - The ID of the current user (for permission checks)
 * 
 * @returns An object containing:
 * - highlights: Array of ExtendedHighlight objects from the backend
 * - isLoading: Boolean indicating if highlights are being fetched
 * - error: Any error that occurred during fetching
 * - addHighlight: Function to create a new persistent highlight
 * - updateHighlight: Function to update an existing highlight
 * - deleteHighlight: Function to delete a highlight (user must own it)
 * - canEditHighlight: Function to check if user can edit a specific highlight
 * - getHighlightById: Function to retrieve a highlight by its ID
 * - isCreating: Boolean indicating if a highlight is being created
 * - isUpdating: Boolean indicating if a highlight is being updated
 * - isDeleting: Boolean indicating if a highlight is being deleted
 * 
 * @example
 * ```tsx
 * const {
 *   highlights,
 *   isLoading,
 *   addHighlight,
 *   canEditHighlight
 * } = usePersistentHighlights(documentId, currentUserId);
 * 
 * // Create a new highlight
 * await addHighlight({
 *   id: 'temp-id',
 *   type: 'text',
 *   position: selectionData,
 *   comment: { text: 'My note', emoji: '📝' }
 * });
 * 
 * // Check permissions
 * if (canEditHighlight(highlightId)) {
 *   // Show edit/delete options
 * }
 * ```
 */
export const usePersistentHighlights = (
  documentId: string,
  currentUserId?: string,
) => {
  const queryClient = useQueryClient();

  // Query to fetch all highlights for the document
  const {
    data: backendHighlights = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["document-highlights", documentId],
    queryFn: () => getDocumentHighlights(documentId),
    enabled: !!documentId,
  });

  // Backend now returns highlights in the correct format
  const highlights: ExtendedHighlight[] = backendHighlights;

  // Create highlight mutation
  const createHighlightMutation = useMutation({
    mutationFn: (highlightData: CreateHighlightRequest) =>
      createHighlight(highlightData),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-highlights", documentId],
      });
    },
    onError: (error) => {
      console.error("Failed to create highlight:", error);
      // Could add toast notification here
    },
  });

  // Update highlight mutation
  const updateHighlightMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateHighlightRequest }) =>
      updateHighlight(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-highlights", documentId],
      });
    },
    onError: (error) => {
      console.error("Failed to update highlight:", error);
      // Could add toast notification here
    },
  });

  // Delete highlight mutation
  const deleteHighlightMutation = useMutation({
    mutationFn: (highlightId: string) => deleteHighlight(highlightId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["document-highlights", documentId],
      });
    },
    onError: (error) => {
      console.error("Failed to delete highlight:", error);
      // Could add toast notification here
    },
  });

  // Add a new highlight
  const addHighlight = useCallback(
    async (highlight: ExtendedHighlight) => {
      if (!documentId) {
        throw new Error("Document ID is required to create highlight");
      }

      try {
        const backendData = convertToBackendHighlight(highlight, documentId);
        return await createHighlightMutation.mutateAsync(backendData);
      } catch (error) {
        console.error("Error adding highlight:", error);
        throw error;
      }
    },
    [documentId, createHighlightMutation],
  );

  // Update an existing highlight
  const updateHighlightById = useCallback(
    async (highlightId: string, updates: UpdateHighlightRequest) => {
      return updateHighlightMutation.mutateAsync({
        id: highlightId,
        data: updates,
      });
    },
    [updateHighlightMutation],
  );

  // Delete a highlight
  const deleteHighlightById = useCallback(
    async (highlightId: string) => {
      const highlight = highlights.find((h) => h.id === highlightId);
      if (!highlight?.userInfo || highlight.userInfo.id !== currentUserId) {
        throw new Error("You can only delete your own highlights");
      }
      return deleteHighlightMutation.mutateAsync(highlightId);
    },
    [deleteHighlightMutation, highlights, currentUserId],
  );

  // Check if user can edit a highlight
  const canEditHighlight = useCallback(
    (highlightId: string) => {
      const highlight = highlights.find((h) => h.id === highlightId);
      return highlight?.userInfo?.id === currentUserId;
    },
    [highlights, currentUserId],
  );

  // Get highlight by ID
  const getHighlightById = useCallback(
    (highlightId: string) => {
      return highlights.find((h) => h.id === highlightId);
    },
    [highlights],
  );

  return {
    highlights,
    isLoading,
    error,
    addHighlight,
    updateHighlight: updateHighlightById,
    deleteHighlight: deleteHighlightById,
    canEditHighlight,
    getHighlightById,
    isCreating: createHighlightMutation.isPending,
    isUpdating: updateHighlightMutation.isPending,
    isDeleting: deleteHighlightMutation.isPending,
  };
};
